import { State } from 'interfaces/state'
import { FutureLayerUID, FutureLayer } from 'components/features/future/types'
import { createSelector } from 'reselect'
import { getItemFromCollection, getItemsFromCollection } from 'helpers/normalizeHelpers'
import { filterTimeframe, findRetireIndex, timeframeToDateRange, isDateInRange } from 'components/features/future/helpers'
import { memberBirthDateSelector } from 'selectors/globalSelectors'
import { createGetAgeMonthPrecision } from 'helpers/dateTimeHelpers'
import find from 'lodash/find'
import findLastIndex from 'lodash/findLastIndex'


export const futureDataSelector = (state: State) => state.data.future

export const layerPostRequestSelector = createSelector(
  futureDataSelector,
  (future) => future.layerPostRequest
)

export const futureRequestSelector = createSelector(
  futureDataSelector,
  (future) => future.request
)

export const layersUIDsSelector = createSelector(
  futureDataSelector,
  (future) => {
    return future.layers.allKeys
  }
)

export const assumptionsSelector = createSelector(
  futureDataSelector,
  (future) => future.assumptions.data
)

export const layersSelector = createSelector(
  futureDataSelector,
  (future) => {
    return getItemsFromCollection(future.layers)
  }
)

export const enabledLayersSelector = createSelector(
  layersSelector,
  (layers) => {
    return layers.filter((layer) => layer.status === 'enabled')
  }
)

export const retireLayerSelector = createSelector(
  layersSelector,
  (layers) => {
    return find(layers, { slug: 'retirement' })
  }
)

export const firstLayersUIDsSelector = createSelector(
  layersSelector,
  memberBirthDateSelector,
  (layers, birthDate) => {

    const index = findLastIndex(layers, (item) => !!item.when_month)
    const layer = layers[index]
    const lastWhen = layer && layer.when_month
    const lastLayer = layers[layers.length - 1]
    if (!lastWhen || !birthDate || !lastLayer || !lastLayer.when_month) {
      return []
    }

    const dateRange = timeframeToDateRange('future', birthDate, lastLayer.when_month)
    const { planA, planB } = layers.reduce((acc, item) => {
      const isWhenExists = !!item.when_month
      const isComplete = item.status === 'completed' || item.status === 'partially_completed' || item.status === 'past'
      const isInRange = !item.when_month ? false : isDateInRange(item.when_month, dateRange)
      const isEligible = isWhenExists && !isComplete && isInRange

      if (isEligible) {
        acc.planA.push(item)
      } else {
        acc.planB.push(item)
      }

      return acc
    }, {
      planA: [] as FutureLayer[],
      planB: [] as FutureLayer[],
    })

    const eligibleLayers = planA.length ? planA : planB

    return eligibleLayers
      .slice(0, 3)
      .map((item) => item.uid)
  }
)

export const networthForecastSelector = createSelector(
  futureDataSelector,
  (state: State) => state.app.future.timeframe,
  memberBirthDateSelector,
  (future, timeframe, birthDate) => {
    if (!birthDate) {
      return []
    }

    const { series } = future.networth_forecast
    if (series.length === 0) {
      return []
    }

    const lastItem = series[series.length - 1]
    return filterTimeframe(series, timeframe, birthDate, lastItem.date)
  }
)

export const networthBreakdownSelector = createSelector(
  futureDataSelector,
  (state: State) => state.app.future.timeframe,
  memberBirthDateSelector,
  (future, timeframe, birthDate) => {
    if (!birthDate) {
      return []
    }

    const { series } = future.networth_breakdown
    if (series.length === 0) {
      return []
    }

    const lastItem = series[series.length - 1]
    return filterTimeframe(series, timeframe, birthDate, lastItem.date)
  }
)

export const layerSelector = createSelector(
  futureDataSelector,
  (_state: State, { uid }: { uid?: FutureLayerUID }) => uid,
  (future, uid) => {
    if (!uid) {
      return null
    }
    return getItemFromCollection(future.layers, uid)
  }
)

export const layerHintsSelector = createSelector(
  futureDataSelector,
  (future) => {
    return future.layerHints
  }
)

export const chartDefaultIndexSelector = createSelector(
  networthBreakdownSelector,
  retireLayerSelector,
  memberBirthDateSelector,
  (items, retireLayer, birthDate) => {
    if (!birthDate) {
      return -1
    }

    const lastIndex = items.length - 1

    if (!retireLayer || retireLayer.status !== 'enabled' || !retireLayer.when_month) {
      return lastIndex
    }

    const retireAge = createGetAgeMonthPrecision(birthDate)(retireLayer.when_month)
    const index = findRetireIndex(items, birthDate, retireAge)
    if (index === -1) {
      return lastIndex
    }
    return index
  }
)

